<script setup lang="ts">
import { nextTick, ref, watch } from 'vue';
import CopyIcon from '@/assets/svg/conversations/copy-01.svg?component';
import CheckIcon from '@/assets/svg/check-16.svg?component';

const props = defineProps<{
  address: string | null;
  displayAddress: string;
  isLoading: boolean;
  hasError: boolean;
  justCopied: boolean;
}>();

const emit = defineEmits<{
  copy: [];
  retry: [];
}>();

const isRevealed = ref(false);
const addressDisplayEl = ref<HTMLElement | null>(null);

watch(
  () => props.address,
  () => {
    isRevealed.value = false;
  },
);

async function toggleReveal(): Promise<void> {
  if (!props.address) return;
  isRevealed.value = !isRevealed.value;
  if (!isRevealed.value) return;

  await nextTick();
  const el = addressDisplayEl.value;
  const selection = window.getSelection();
  if (!el || !selection) return;

  const range = document.createRange();
  range.selectNodeContents(el);
  selection.removeAllRanges();
  selection.addRange(range);
}
</script>

<template>
  <div class="flex w-full flex-col gap-3">
    <div
      class="flex items-center justify-between gap-3 rounded-lg border border-tint-80 bg-white px-4 py-2.5"
    >
      <span v-if="isLoading" class="flex items-center gap-1.5 font-sans text-sm text-shade-600">
        Loading address
        <span class="flex items-end gap-0.5" data-test="loading-dots">
          <span class="h-1 w-1 animate-bounce rounded-full bg-shade-600 [animation-delay:-0.3s]" />
          <span class="h-1 w-1 animate-bounce rounded-full bg-shade-600 [animation-delay:-0.15s]" />
          <span class="h-1 w-1 animate-bounce rounded-full bg-shade-600" />
        </span>
      </span>
      <span
        v-else
        ref="addressDisplayEl"
        data-test="intake-address-display"
        :role="address ? 'button' : undefined"
        :tabindex="address ? 0 : undefined"
        :title="
          address
            ? isRevealed
              ? 'Click to collapse'
              : 'Click to select the full address'
            : undefined
        "
        class="font-mono min-w-0 flex-1 whitespace-nowrap text-sm text-shade-900"
        :class="[
          isRevealed ? 'select-text overflow-x-auto' : 'select-none truncate',
          address ? 'cursor-pointer' : '',
        ]"
        @click="toggleReveal"
        @keydown.enter="toggleReveal"
        @keydown.space.prevent="toggleReveal"
        >{{ isRevealed ? address : displayAddress }}</span
      >
      <button
        type="button"
        data-test="copy-address"
        :disabled="!address"
        class="flex shrink-0 items-center gap-1.5 font-sans text-sm font-medium text-shade-800 hover:text-shade-900 disabled:cursor-not-allowed disabled:text-shade-500 disabled:hover:text-shade-500"
        @click="emit('copy')"
      >
        <template v-if="justCopied">
          <CheckIcon class="h-4 w-4 text-primary-500" />
          <span class="text-primary-500">Copied</span>
        </template>
        <template v-else>
          <CopyIcon class="h-4 w-4" />
          Copy
        </template>
      </button>
    </div>

    <div v-if="hasError" class="flex items-center gap-1.5 font-sans text-sm text-critical-600">
      Couldn't load your intake address.
      <button
        type="button"
        data-test="retry-fetch"
        class="font-semibold underline hover:text-critical-700"
        @click="emit('retry')"
      >
        Try again
      </button>
    </div>
  </div>
</template>
